<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Automatic Employee Alerts
      </oxd-text>
      <oxd-divider />

      <div
        v-if="message"
        class="orangehrm-alerts-message"
        :class="{
          '--success': messageType === 'success',
          '--error': messageType === 'error',
        }"
      >
        {{ message }}
      </div>

      <form method="post" :action="currentUrl">
        <input v-if="configuration.enabled" type="hidden" name="enabled" value="1" />
        <input
          v-if="automation.punchInReminder"
          type="hidden"
          name="automation_punch_in_reminder"
          value="1"
        />
        <input
          v-if="automation.missingPunchOutReminder"
          type="hidden"
          name="automation_missing_punch_out_reminder"
          value="1"
        />
        <input
          v-if="automation.incompleteAttendanceMorning"
          type="hidden"
          name="automation_incomplete_attendance_morning"
          value="1"
        />
        <input
          v-if="automation.shortAttendanceReminder"
          type="hidden"
          name="automation_short_attendance_reminder"
          value="1"
        />
        <input
          v-if="automation.correctionLimitWarning"
          type="hidden"
          name="automation_correction_limit_warning"
          value="1"
        />
        <input
          v-if="automation.leaveStatusUpdate"
          type="hidden"
          name="automation_leave_status_update"
          value="1"
        />
        <input
          v-if="automation.upcomingLeaveReminder"
          type="hidden"
          name="automation_upcoming_leave_reminder"
          value="1"
        />
        <input
          type="hidden"
          name="automation_punch_in_minutes_before"
          :value="automation.punchInMinutesBefore"
        />
        <input
          type="hidden"
          name="automation_missing_punch_out_minutes_after"
          :value="automation.missingPunchOutMinutesAfter"
        />
        <input
          type="hidden"
          name="automation_incomplete_morning_time"
          :value="automation.incompleteMorningTime"
        />
        <input
          type="hidden"
          name="automation_upcoming_leave_days_before"
          :value="automation.upcomingLeaveDaysBefore"
        />
        <input
          type="hidden"
          name="automation_work_shift_ids"
          :value="selectedWorkShiftIds"
        />
        <input type="hidden" name="action" :value="formAction" />

        <oxd-form-row>
          <div class="orangehrm-alerts-switch">
            <div>
              <oxd-text tag="p" class="orangehrm-module-field-label">
                GroupHR In-App Notifications
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-alerts-help">
                Automatic alerts create inbox messages inside the employee PWA.
              </oxd-text>
            </div>
            <oxd-switch-input v-model="configuration.enabled" />
          </div>
        </oxd-form-row>

        <oxd-divider class="orangehrm-form-divider" />

        <div class="orangehrm-alerts-scope">
          <div>
            <strong>Alert Audience</strong>
            <span>{{ workShiftSummary }}</span>
          </div>
          <div class="orangehrm-alerts-shifts">
            <label>
              <span>Work Shifts</span>
              <select v-model="automation.workShiftIds" multiple size="6">
                <option
                  v-for="shift in workShifts"
                  :key="shift.id"
                  :value="String(shift.id)"
                >
                  {{ shift.name }} - {{ shift.startTime }} to {{ shift.endTime }} ({{ shift.employeeCount }} employees)
                </option>
              </select>
            </label>
            <div class="orangehrm-alerts-scope-actions">
              <button type="button" @click="selectAllWorkShifts">
                Select All
              </button>
              <button type="button" @click="clearWorkShifts">
                All Work Shifts
              </button>
            </div>
            <em>Leave empty to send automatic alerts to all active work shifts.</em>
          </div>
        </div>

        <oxd-divider class="orangehrm-form-divider" />

        <div class="orangehrm-alerts-grid">
          <label class="orangehrm-alerts-toggle">
            <input v-model="automation.punchInReminder" type="checkbox" />
            <span>
              <strong>Punch-In Reminder</strong>
              <em>Before shift start, if the employee has not punched in.</em>
            </span>
          </label>
          <label class="orangehrm-alerts-toggle">
            <input v-model="automation.missingPunchOutReminder" type="checkbox" />
            <span>
              <strong>Missing Punch-Out Reminder</strong>
              <em>After shift end, if the employee is still punched in.</em>
            </span>
          </label>
          <label class="orangehrm-alerts-toggle">
            <input v-model="automation.incompleteAttendanceMorning" type="checkbox" />
            <span>
              <strong>Incomplete Attendance Next Morning</strong>
              <em>For previous-day punch records with no valid punch out.</em>
            </span>
          </label>
          <label class="orangehrm-alerts-toggle">
            <input v-model="automation.shortAttendanceReminder" type="checkbox" />
            <span>
              <strong>Short Attendance Correction Reminder</strong>
              <em>After shift end, if worked hours are Half Day or Absent by schedule.</em>
            </span>
          </label>
          <label class="orangehrm-alerts-toggle">
            <input v-model="automation.correctionLimitWarning" type="checkbox" />
            <span>
              <strong>Correction Limit Warning</strong>
              <em>When approved monthly correction requests reach the warning level.</em>
            </span>
          </label>
          <label class="orangehrm-alerts-toggle">
            <input v-model="automation.leaveStatusUpdate" type="checkbox" />
            <span>
              <strong>Leave Status Update</strong>
              <em>When OrangeHRM leave changes to approved, rejected, or cancelled.</em>
            </span>
          </label>
          <label class="orangehrm-alerts-toggle">
            <input v-model="automation.upcomingLeaveReminder" type="checkbox" />
            <span>
              <strong>Upcoming Leave Reminder</strong>
              <em>Before approved leave starts.</em>
            </span>
          </label>
        </div>

        <oxd-divider class="orangehrm-form-divider" />

        <div class="orangehrm-alerts-timing">
          <label>
            <span>Punch-In Reminder</span>
            <input
              v-model.number="automation.punchInMinutesBefore"
              type="number"
              min="1"
              max="120"
            />
            <em>minutes before shift start</em>
          </label>
          <label>
            <span>Missing Punch-Out</span>
            <input
              v-model.number="automation.missingPunchOutMinutesAfter"
              type="number"
              min="1"
              max="240"
            />
            <em>minutes after shift end</em>
          </label>
          <label>
            <span>Incomplete Attendance</span>
            <input v-model="automation.incompleteMorningTime" type="time" />
            <em>next morning after this time</em>
          </label>
          <label>
            <span>Upcoming Leave</span>
            <input
              v-model.number="automation.upcomingLeaveDaysBefore"
              type="number"
              min="1"
              max="14"
            />
            <em>day(s) before leave start</em>
          </label>
        </div>

        <div class="orangehrm-alerts-run">
          <div>
            <strong>{{ automationSummaryText }}</strong>
            <span>{{ automationSummaryDetails }}</span>
          </div>
          <oxd-button
            display-type="secondary"
            label="Run Automation Now"
            type="submit"
            @click="formAction = 'run_automations'"
          />
        </div>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            label="Notifications"
            type="button"
            @click="openNotifications"
          />
          <oxd-button
            display-type="secondary"
            label="Save"
            type="submit"
            @click="formAction = 'save'"
          />
        </oxd-form-actions>
      </form>
    </div>
  </div>
</template>

<script>
import {OxdSwitchInput} from '@ohrm/oxd';

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
    automationSettings: {
      type: Object,
      default: () => ({}),
    },
    automationSummary: {
      type: Object,
      default: () => ({}),
    },
    message: {
      type: String,
      default: '',
    },
    messageType: {
      type: String,
      default: '',
    },
    notificationsUrl: {
      type: String,
      required: true,
    },
    workShifts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      configuration: {...this.settings},
      automation: this.buildAutomationSettings(),
      currentUrl: window.location.href,
      formAction: 'save',
    };
  },
  computed: {
    automationSummaryText() {
      if (!this.automationSummary || !this.automationSummary.ranAt) {
        return 'Automation is ready';
      }
      return `${this.automationSummary.sent || 0} notification(s) created`;
    },
    automationSummaryDetails() {
      if (!this.automationSummary || !Array.isArray(this.automationSummary.details)) {
        return 'Run manually here, then schedule the PHP runner on the server.';
      }
      return this.automationSummary.details.join(' | ');
    },
    selectedWorkShiftIds() {
      const ids = Array.isArray(this.automation.workShiftIds)
        ? this.automation.workShiftIds
        : [];
      return ids.map((id) => String(id)).join(',');
    },
    workShiftSummary() {
      const ids = Array.isArray(this.automation.workShiftIds)
        ? this.automation.workShiftIds
        : [];
      if (ids.length === 0) {
        return 'Automatic alerts will run for all active employees.';
      }
      const selected = this.workShifts.filter((shift) =>
        ids.includes(String(shift.id)),
      );
      const employeeCount = selected.reduce(
        (total, shift) => total + Number(shift.employeeCount || 0),
        0,
      );
      return `${ids.length} work shift(s) selected, about ${employeeCount} employee(s).`;
    },
  },
  methods: {
    buildAutomationSettings() {
      const settings = {
        punchInReminder: true,
        missingPunchOutReminder: true,
        incompleteAttendanceMorning: true,
        shortAttendanceReminder: true,
        correctionLimitWarning: true,
        leaveStatusUpdate: true,
        upcomingLeaveReminder: true,
        punchInMinutesBefore: 15,
        missingPunchOutMinutesAfter: 30,
        incompleteMorningTime: '08:00',
        upcomingLeaveDaysBefore: 1,
        ...this.automationSettings,
      };
      settings.workShiftIds = Array.isArray(settings.workShiftIds)
        ? settings.workShiftIds.map((id) => String(id))
        : [];
      return settings;
    },
    openNotifications() {
      window.location.href = this.notificationsUrl;
    },
    selectAllWorkShifts() {
      this.automation.workShiftIds = this.workShifts.map((shift) =>
        String(shift.id),
      );
    },
    clearWorkShifts() {
      this.automation.workShiftIds = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-alerts-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #e8eaef;
  border-radius: 0.65rem;
  padding: 0.85rem 1rem;
}

.orangehrm-alerts-help {
  color: #64728c;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.orangehrm-alerts-message {
  border-radius: 0.65rem;
  font-weight: 700;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;

  &.--success {
    background: #edf7e7;
    color: #386b12;
  }

  &.--error {
    background: #fff1f1;
    color: #b42318;
  }
}

.orangehrm-alerts-grid,
.orangehrm-alerts-timing {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.orangehrm-alerts-scope {
  display: grid;
  grid-template-columns: minmax(13rem, 0.75fr) minmax(0, 1.25fr);
  gap: 1rem;
  border: 1px solid #e8eaef;
  border-radius: 0.65rem;
  background: #fbfcfe;
  padding: 1rem;

  strong,
  span,
  em {
    display: block;
  }

  strong {
    color: #38455d;
    font-size: 0.85rem;
  }

  span,
  em {
    color: #8a96ab;
    font-size: 0.72rem;
    font-style: normal;
    font-weight: 700;
    margin-top: 0.25rem;
  }
}

.orangehrm-alerts-shifts {
  display: grid;
  gap: 0.6rem;

  label {
    display: grid;
    gap: 0.4rem;
    color: #64728c;
    font-size: 0.75rem;
    font-weight: 700;
  }

  select {
    min-height: 10.5rem;
    border: 1px solid #e8eaef;
    border-radius: 0.65rem;
    background: #fff;
    color: #38455d;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.45rem;
  }

  option {
    padding: 0.35rem 0.45rem;
  }
}

.orangehrm-alerts-scope-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  button {
    min-height: 2rem;
    border: 0;
    border-radius: 999px;
    background: #fff1e8;
    color: #c44f10;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 800;
    padding: 0 0.85rem;
  }
}

.orangehrm-alerts-toggle {
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
  min-height: 4.3rem;
  border: 1px solid #edf0f5;
  border-radius: 0.65rem;
  background: #fff;
  padding: 0.8rem;

  input {
    flex: 0 0 auto;
    margin-top: 0.15rem;
  }

  span,
  strong,
  em {
    display: block;
  }

  strong {
    color: #38455d;
    font-size: 0.8rem;
  }

  em {
    color: #8a96ab;
    font-size: 0.68rem;
    font-style: normal;
    margin-top: 0.25rem;
  }
}

.orangehrm-alerts-timing {
  label {
    display: grid;
    gap: 0.35rem;
    color: #64728c;
    font-size: 0.75rem;
    font-weight: 700;
  }

  input {
    width: 100%;
    min-height: 2.75rem;
    border: 1px solid #e8eaef;
    border-radius: 0.65rem;
    background: #fff;
    color: #38455d;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0 0.75rem;
  }

  em {
    color: #8a96ab;
    font-size: 0.68rem;
    font-style: normal;
  }
}

.orangehrm-alerts-run {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #e8eaef;
  border-radius: 0.65rem;
  margin: 1rem 0 1.25rem;
  padding: 0.85rem 1rem;

  strong,
  span {
    display: block;
  }

  strong {
    color: #38455d;
    font-size: 0.85rem;
  }

  span {
    color: #8a96ab;
    font-size: 0.72rem;
    font-weight: 700;
    margin-top: 0.2rem;
  }
}

@media (max-width: 760px) {
  .orangehrm-alerts-switch,
  .orangehrm-alerts-scope,
  .orangehrm-alerts-run {
    align-items: stretch;
    flex-direction: column;
  }

  .orangehrm-alerts-scope,
  .orangehrm-alerts-grid,
  .orangehrm-alerts-timing {
    grid-template-columns: 1fr;
  }
}
</style>
